import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_WEIGHT, CS_Z, csTokens } from 'components/CallScribe/callScribeTheme';
import { mixHex } from 'components/CallScribe/ui/color';

const useActionBarStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    bar: {
      position: 'sticky',
      bottom: 0,
      zIndex: CS_Z.sticky,
      flex: '0 0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      padding: '12px 16px 16px',
      borderTop: `1px solid ${t.line}`,
      background: t.bg,
    },
    action: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      flex: '1 1 auto',
      width: '100%',
      minWidth: 0,
      minHeight: 52,
      padding: '0 16px',
      boxSizing: 'border-box',
      border: 'none',
      borderRadius: 12,
      fontFamily: 'inherit',
      fontSize: 16,
      lineHeight: '22px',
      fontWeight: CS_WEIGHT.strong,
      background: t.call,
      color: t.callInk,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, transform 120ms ease-out',
      '& svg': { fontSize: 20 },
      '&:hover': { background: mixHex(t.call, t.text, 0.86) },
      '&:active': { transform: 'scale(0.99)' },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&[aria-disabled="true"]': {
        background: t.surface3,
        color: t.text2,
        cursor: 'not-allowed',
        transform: 'none',
      },
      '&[aria-busy="true"]': { cursor: 'progress', transform: 'none' },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'background-color 150ms ease-out',
        '&:active': { transform: 'none' },
      },
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minWidth: 0,
    },
    secondary: {
      flex: '0 0 auto',
      minHeight: 52,
      padding: '0 16px',
      border: 'none',
      borderRadius: 12,
      background: 'transparent',
      color: t.text2,
      fontFamily: 'inherit',
      fontSize: 15,
      lineHeight: '22px',
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, color 150ms ease-out',
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&[aria-disabled="true"]': { cursor: 'not-allowed', background: 'transparent', color: t.text2 },
    },
    text: {
      fontSize: 'inherit',
      lineHeight: 'inherit',
      fontWeight: 'inherit',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
  };
});

export interface IActionBarSecondary {
  label: string;
  onAction: () => void;
  disabled?: boolean;
}

export interface IActionBarProps {
  label: string;
  onAction: () => void;
  disabledReason?: string;
  busy?: boolean;
  busyLabel?: string;
  icon?: React.ReactElement;
  secondary?: IActionBarSecondary;
  children?: React.ReactNode;
}

const ActionBar: React.FC<IActionBarProps> = ({
  label,
  onAction,
  disabledReason,
  busy = false,
  busyLabel,
  icon,
  secondary,
  children,
}) => {
  const classes = useActionBarStyles();
  const blocked = Boolean(disabledReason);
  const text = busy ? busyLabel ?? label : disabledReason ?? label;
  return (
    <div className={classes.bar} data-action-bar>
      {children}
      <div className={classes.row}>
        <button
          type="button"
          className={classes.action}
          aria-disabled={blocked || undefined}
          aria-busy={busy || undefined}
          onClick={() => {
            if (blocked || busy) return;
            onAction();
          }}
        >
          {icon && !blocked ? React.cloneElement(icon, { 'aria-hidden': true }) : null}
          <span className={classes.text}>{text}</span>
        </button>
        {secondary ? (
          <button
            type="button"
            className={classes.secondary}
            aria-disabled={secondary.disabled === true ? true : undefined}
            onClick={() => {
              if (secondary.disabled) return;
              secondary.onAction();
            }}
          >
            {secondary.label}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ActionBar;
